/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { toast } from 'react-toastify';

import { all, takeLatest, call, put } from 'redux-saga/effects';

import api, { clearLocalStorage } from '~/services/api';
import history from '~/services/history';

import { clearCepRequest } from '../cep/actions';
import {
  getUsersSuccess,
  getUsersFailure,
  addUserSuccess,
  addUserFailure,
  changeUserPasswordSuccess,
  changeUserPasswordFailure,
  activateUserSuccess,
  activateUserFailure,
  getGroupsSuccess,
  getGroupsFailure,
  getUserGroupsSuccess,
  getUserGroupsFailure,
  addUserGroupSuccess,
  addUserGroupFailure,
  removeUserGroupSuccess,
  removeUserGroupFailure,
  getProfilesSuccess,
  getProfilesFailure,
  getUserProfilesSuccess,
  getUserProfilesFailure,
  addUserProfileSuccess,
  addUserProfileFailure,
  removeUserProfileSuccess,
  removeUserProfileFailure,
  getCamundaUsersSuccess,
  getCamundaUsersFailure,
  getActivatedUsersSuccess,
  getActivatedUsersFailure,
  getUsersByGroupsSuccess,
  getUsersByGroupsFailure,
  getUserProfileSuccess,
  getUserProfileFailure,
  updateUserProfileSuccess,
  updateUserProfileFailure,
  getUserByApplicationIdAndEmailSuccess,
  getUserByApplicationIdAndEmailFailure,
} from './actions';
import {
  IUsersTypes,
  IGetUsersRequestAction,
  IAddUserRequestAction,
  IChangeUserPasswordRequestAction,
  IActivateUserRequestAction,
  IGetUserGroupsRequestAction,
  IAddUserGroupRequestAction,
  IRemoveUserGroupRequestAction,
  IGetUserProfilesRequestAction,
  IAddUserProfileRequestAction,
  IRemoveUserProfileRequestAction,
  IProfile,
  IGetUsersByGroupsRequestAction,
  IGetActivatedUsersRequestAction,
  IGetUserProfileRequestAction,
  IUpdateUserProfileRequestAction,
  IGetUserByApplicationIdAndEmailRequestAction,
} from './types';

function* getUsers({ payload }: IGetUsersRequestAction) {
  const { page, limit, search } = payload;

  try {
    const response = yield call(api.get, '/users', {
      params: { page, limit, search },
    });

    const users = response.data;
    const { pagecount, total } = response.headers;

    yield put(getUsersSuccess(users, pagecount, total));
  } catch (err) {
    yield put(getUsersFailure());
    toast.error('Erro de conexão, tente mais tarde.');
  }
}

function* getUserByApplicationIdAndEmail({
  payload,
}: IGetUserByApplicationIdAndEmailRequestAction) {
  const { applicationId, email } = payload;

  try {
    const response = yield call(api.get, `/users/${applicationId}/${email}`);

    const user = response.data;

    yield put(getUserByApplicationIdAndEmailSuccess(user));
  } catch (err) {
    yield put(getUserByApplicationIdAndEmailFailure());
  }
}

function* addUser({ payload }: IAddUserRequestAction) {
  const { data } = payload;

  try {
    yield call(api.post, '/users', data);

    yield put(addUserSuccess());
    yield put(clearCepRequest());
    history.push('/users');
    toast.success('Usuário cadastrado com sucesso.');
  } catch (err) {
    yield put(addUserFailure());
    toast.error('Falha ao cadastrar o usuário.');
  }
}

function* changeUserPassword({ payload }: IChangeUserPasswordRequestAction) {
  const { id, managerId, password, newPassword, personId, userEmail } = payload;

  try {
    if (password) {
      yield call(api.put, `/users/${id}/change-password`, {
        password,
        newPassword,
        personId,
      });
    } else {
      yield call(api.put, `/users/${id}/reset-password`, {
        managerId,
      });
    }

    yield put(changeUserPasswordSuccess());
    yield put(clearCepRequest());
    if (password) {
      toast.success(`Senha alterada com sucesso`);
    } else {
      toast.success(`Nova senha encaminhada com sucesso para ${userEmail}`);
    }
  } catch (err) {
    yield put(changeUserPasswordFailure());
    toast.error(
      'Falha ao atualizar a senha, verifique se a senha atual está correta.',
    );
  }
}

function* activateUser({ payload }: IActivateUserRequestAction) {
  const { user_id, activate } = payload;

  try {
    yield call(api.post, `/users/${user_id}/activate`, {
      activate,
    });

    yield put(activateUserSuccess(user_id, activate));
  } catch (err) {
    yield put(activateUserFailure());
    toast.error('Falha ao ativar/inativar usuário.');
  }
}

function* getGroups() {
  try {
    const response = yield call(api.get, '/groups');

    const groups = response.data;

    yield put(getGroupsSuccess(groups));
  } catch (err) {
    yield put(getGroupsFailure());
    toast.error('Falha ao listar as funções.');
  }
}

function* getUserGroups({ payload }: IGetUserGroupsRequestAction) {
  const { user_id } = payload;

  try {
    const response = yield call(api.get, `/users/${user_id}/groups`);

    const groups = response.data;

    yield put(getUserGroupsSuccess(user_id, groups));
  } catch (err) {
    yield put(getUserGroupsFailure());
    toast.error('Falha ao listar as funções do usuário.');
  }
}

function* addUserGroup({ payload }: IAddUserGroupRequestAction) {
  const { user_id, group } = payload;

  try {
    yield call(api.put, `/users/${user_id}/groups/${group.id}`);

    yield put(addUserGroupSuccess(user_id, group));
    toast.success('Função adicionada com sucesso.');
  } catch (err) {
    yield put(addUserGroupFailure());
    toast.error('Falha ao adicionar a função no usuário.');
  }
}

function* removeUserGroup({ payload }: IRemoveUserGroupRequestAction) {
  const { user_id, group } = payload;

  try {
    yield call(api.delete, `/users/${user_id}/groups/${group.id}`);

    yield put(removeUserGroupSuccess(user_id, group));
    toast.success('Função removida com sucesso.');
  } catch (err) {
    yield put(removeUserGroupFailure());
    toast.error('Falha ao remover a função do usuário.');
  }
}

function* getProfiles() {
  try {
    const response = yield call(api.get, '/profiles');

    const profiles = response.data;

    const filteredProfiles = profiles.filter((profile: IProfile) =>
      profile.profileKey.startsWith('WEDO_'),
    );

    yield put(getProfilesSuccess(filteredProfiles));
  } catch (err) {
    yield put(getProfilesFailure());
    toast.error('Falha ao listar os perfis.');
  }
}

function* getUserProfile({ payload }: IGetUserProfileRequestAction) {
  const { personId } = payload;

  try {
    const response = yield call(api.get, `/users/${personId}/profile`);

    const { data } = response;

    yield put(getUserProfileSuccess(data));
  } catch (err) {
    yield put(getUserProfileFailure());
    toast.error('Falha ao listar o perfil do usuário.');
  }
}

function* getUserProfiles({ payload }: IGetUserProfilesRequestAction) {
  const { user_id } = payload;

  try {
    const response = yield call(api.get, `/users/${user_id}/profiles`);

    const { profiles } = response.data;

    yield put(getUserProfilesSuccess(user_id, profiles));
  } catch (err) {
    yield put(getUserProfilesFailure());
    toast.error('Falha ao listar os perfis do usuário.');
  }
}

function* addUserProfile({ payload }: IAddUserProfileRequestAction) {
  const { user_id, profile } = payload;

  try {
    yield call(api.post, `/users/${user_id}/profiles`, profile);

    yield put(addUserProfileSuccess(user_id, profile));
    toast.success('Perfil adicionado com sucesso.');
  } catch (err) {
    yield put(addUserProfileFailure());
    toast.error('Falha ao adicionar o perfil no usuário.');
  }
}

function* updateUserProfile({ payload }: IUpdateUserProfileRequestAction) {
  const { personId, userProfile } = payload;

  try {
    const response = yield call(api.put, `/users/${personId}`, userProfile);
    const person = yield call(api.get, `/users/${personId}/profile`);

    yield put(updateUserProfileSuccess(response.data));
    yield put(getUserProfileSuccess(person.data));
    toast.success('perfil de usuário editado com sucesso');
  } catch (err) {
    yield put(updateUserProfileFailure());
    toast.error('Falha ao editar o perfil do usuário.');
  }
}

function* removeUserProfile({ payload }: IRemoveUserProfileRequestAction) {
  const { user_id, profile } = payload;

  try {
    yield call(api.delete, `/users/${user_id}/profiles/${profile.id}`);

    yield put(removeUserProfileSuccess(user_id, profile));
    toast.success('Perfil removido com sucesso.');
  } catch (err) {
    yield put(removeUserProfileFailure());
    toast.error('Falha ao remover o perfil do usuário.');
  }
}

function* getCamundaUsers() {
  try {
    const response = yield call(api.get, '/users/camunda');

    const camundaUsers = response.data;

    yield put(getCamundaUsersSuccess(camundaUsers));
  } catch (err) {
    yield put(getCamundaUsersFailure());
  }
}

function* getActivatedUsers({ payload }: IGetActivatedUsersRequestAction) {
  const { profileKey } = payload;

  try {
    const response = yield call(api.get, '/users', {
      params: { profileKey, onlyActive: true },
    });

    const activeUsers = response.data;

    yield put(getActivatedUsersSuccess(activeUsers));
  } catch (err) {
    yield put(getActivatedUsersFailure());
  }
}

function* getUsersByGroups({
  payload,
}: IGetUsersByGroupsRequestAction): unknown {
  const { groups } = payload;

  try {
    const response = yield call(api.get, `/groups/users`, {
      params: { groups },
    });

    const usersByGroups = response.data;

    yield put(getUsersByGroupsSuccess(usersByGroups));
  } catch (err) {
    yield put(getUsersByGroupsFailure());
    toast.error('Falha ao listar as funções do usuário.');
  }
}

export default all([
  takeLatest(IUsersTypes.GET_USERS_REQUEST, getUsers),
  takeLatest(
    IUsersTypes.GET_USER_BY_APPLICATION_ID_AND_EMAIL_REQUEST,
    getUserByApplicationIdAndEmail,
  ),
  takeLatest(IUsersTypes.ADD_USER_REQUEST, addUser),
  takeLatest(IUsersTypes.CHANGE_USER_PASSWORD_REQUEST, changeUserPassword),
  takeLatest(IUsersTypes.ACTIVATE_USER_REQUEST, activateUser),
  takeLatest(IUsersTypes.GET_GROUPS_REQUEST, getGroups),
  takeLatest(IUsersTypes.GET_USER_GROUPS_REQUEST, getUserGroups),
  takeLatest(IUsersTypes.ADD_USER_GROUP_REQUEST, addUserGroup),
  takeLatest(IUsersTypes.REMOVE_USER_GROUP_REQUEST, removeUserGroup),
  takeLatest(IUsersTypes.GET_PROFILES_REQUEST, getProfiles),
  takeLatest(IUsersTypes.GET_USER_PROFILE_REQUEST, getUserProfile),
  takeLatest(IUsersTypes.GET_USER_PROFILES_REQUEST, getUserProfiles),
  takeLatest(IUsersTypes.ADD_USER_PROFILE_REQUEST, addUserProfile),
  takeLatest(IUsersTypes.UPDATE_USER_PROFILE_REQUEST, updateUserProfile),
  takeLatest(IUsersTypes.REMOVE_USER_PROFILE_REQUEST, removeUserProfile),
  takeLatest(IUsersTypes.GET_CAMUNDA_USERS_REQUEST, getCamundaUsers),
  takeLatest(IUsersTypes.GET_ACTIVATED_USERS_REQUEST, getActivatedUsers),
  takeLatest(IUsersTypes.GET_USERS_BY_GROUPS_REQUEST, getUsersByGroups),
]);
